import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProject, createProject, updateProject, updateProjectChildren } from '../adminApi';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, X, Image, Video, PlayCircle } from 'lucide-react';
import { UploadField, UploadButton } from '../components/UploadField';
import { SortableList, DragHandle } from '../components/SortableList';

// ── Constants ─────────────────────────────────────────────────────────────────

// DB CHECK constraint allows ONLY these three values
const STATUS_OPTIONS = [
  { value: 'ongoing',   label: 'Devam Ediyor' },
  { value: 'completed', label: 'Tamamlandı' },
  { value: 'planning',  label: 'Planlama' },
];

// Canonical map — must stay in sync with backend _STATUS_LABELS and api.js
const STATUS_LABELS = {
  completed: 'Tamamlandı',
  ongoing:   'Devam Ediyor',
  planning:  'Planlama',
};

const LOCATION_OPTIONS = ['Meram', 'Selçuklu', 'Karatay'];

const TYPE_OPTIONS = ['Villa', 'Apartman', 'Rezidans', 'Ticari', 'Kooperatif', 'Site'];

// ── Slug helpers ──────────────────────────────────────────────────────────────

const _TR_SLUG = { 'ğ':'g','ü':'u','ş':'s','ı':'i','ö':'o','ç':'c','Ğ':'g','Ü':'u','Ş':'s','İ':'i','Ö':'o','Ç':'c' };

function normalizeSlug(text) {
  return text
    .split('').map((c) => _TR_SLUG[c] ?? c).join('')
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function genId() {
  return `_${Math.random().toString(36).slice(2, 9)}`;
}

// ── YouTube helpers ───────────────────────────────────────────────────────────

function toYouTubeEmbed(url) {
  if (!url) return url;
  const s = url.trim();
  if (s.includes('youtube.com/embed/')) return s;
  let id = null;
  const w  = s.match(/[?&]v=([a-zA-Z0-9_-]{11})/);    if (w)  id = w[1];
  const b  = s.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/); if (b)  id = b[1];
  const sh = s.match(/shorts\/([a-zA-Z0-9_-]{11})/);    if (sh) id = sh[1];
  if (!id) return s;
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&controls=0&rel=0&modestbranding=1&playlist=${id}`;
}

function getYouTubeThumbnail(embedUrl) {
  const m = (embedUrl || '').match(/\/embed\/([a-zA-Z0-9_-]{11})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
}

const GALLERY_TYPE_OPTIONS = [
  { value: 'image',   label: 'Görsel',        icon: Image },
  { value: 'video',   label: 'Video (MP4)',   icon: Video },
  { value: 'youtube', label: 'YouTube',       icon: PlayCircle },
];

// ── Empty states ──────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  slug: '', title: '',
  status: 'ongoing',
  location: 'Meram', year: String(new Date().getFullYear()),
  units: '', area: '', type: 'Villa', address: '',
  short_description: '', description: '',
  cover_image: '', cover_video: '',
  display_order: 999, is_published: false,
  seo_title: '', seo_description: '',
  construction_area: '', green_area: '', blocks: '',
  shop_count: '', floors: '', delivery: '',
  lat: '', lng: '',
};

const EMPTY_CHILDREN = {
  gallery:            [],
  features:           [],
  materials:          [],
  nearby:             [],
  floor_plan_groups:  [],
};

// ── Data mappers ──────────────────────────────────────────────────────────────

function toForm(p) {
  return {
    slug:              p.slug              ?? '',
    title:             p.title             ?? '',
    status:            p.status            ?? 'ongoing',
    location:          p.location          ?? 'Meram',
    year:              p.year              ?? '',
    units:             p.units             ?? '',
    area:              p.area              ?? '',
    type:              p.type              ?? 'Villa',
    address:           p.address           ?? '',
    short_description: p.short_description ?? '',
    description:       p.description       ?? '',
    cover_image:       p.cover_image       ?? '',
    cover_video:       p.cover_video       ?? '',
    display_order:     p.display_order     ?? 999,
    is_published:      p.is_published      ?? false,
    seo_title:         p.seo_title         ?? '',
    seo_description:   p.seo_description   ?? '',
    construction_area: p.construction_area ?? '',
    green_area:        p.green_area        ?? '',
    blocks:            p.blocks            ?? '',
    shop_count:        p.shop_count        ?? '',
    floors:            p.floors            ?? '',
    delivery:          p.delivery          ?? '',
    lat:               p.lat               ?? '',
    lng:               p.lng               ?? '',
  };
}

function toChildren(p) {
  return {
    gallery: (p.gallery || []).map((g) => ({
      _id:        genId(),
      url:        g.url        || '',
      media_type: g.media_type || 'image',
      poster_url: g.poster_url || '',
      thumb_url:  g.thumb_url  || '',
    })),
    features: (p.features || []).map((f) => ({
      _id:  genId(),
      text: f.text || f || '',
    })),
    materials: (p.materials || []).map((m) => ({
      _id:         genId(),
      title:       m.title       || '',
      description: m.description || '',
      brand_image: m.brand_image || '',
    })),
    nearby: (p.nearby || []).map((n) => ({
      _id:  genId(),
      text: n.text || n || '',
    })),
    floor_plan_groups: (() => {
      const groups = [];
      const seen = new Map();
      (p.floor_plans || []).forEach((fp) => {
        const key = fp.type_title || '';
        if (!seen.has(key)) {
          const g = { _id: genId(), type_title: key, plans: [] };
          seen.set(key, g);
          groups.push(g);
        }
        // label may be JSON-encoded {h, d} (new format) or plain text (legacy)
        let heading = fp.label || '';
        let detailsFromLabel = [];
        try {
          const parsed = JSON.parse(fp.label || '');
          if (parsed && typeof parsed === 'object' && ('h' in parsed || 'd' in parsed)) {
            heading = parsed.h || '';
            detailsFromLabel = Array.isArray(parsed.d) ? parsed.d : [];
          }
        } catch { /* plain text label — keep as-is */ }

        // JSONB column takes priority over label-encoded details when present
        const rawDetails = (fp.details && fp.details.length > 0)
          ? fp.details
          : detailsFromLabel;

        seen.get(key).plans.push({
          _id:       genId(),
          image_url: fp.image_url || '',
          label:     heading,
          details:   rawDetails.map((d) => ({ _id: genId(), name: d.name || '', size: d.size || '' })),
        });
      });
      return groups;
    })(),
  };
}

// Parses a combined "Room Name 25m2" or "Room Name-25m2" into { name, size }.
// Runs at save time so a user who types both values in one field gets clean data.
const _MEASURE_RE = /^(.+)[\s\-–]+([\d,.]+\s*m[²2]?)$/i;
function cleanDetailRow(rawName, rawSize) {
  const name = (rawName || '').trim();
  const size = (rawSize || '').trim();
  // Both fields filled → use as-is
  if (name && size) return { name, size };
  // Name field contains combined "Salon 25m²" → split it
  if (name) {
    const m = name.match(_MEASURE_RE);
    if (m) return { name: m[1].trim(), size: m[2].trim() };
  }
  // Size field has everything (name field empty) → try to split, else move to name
  if (size && !name) {
    const m = size.match(_MEASURE_RE);
    if (m) return { name: m[1].trim(), size: m[2].trim() };
    return { name: size, size: '' };
  }
  return { name, size };
}

function toPayload(form) {
  return {
    ...form,
    status_label: STATUS_LABELS[form.status] || form.status,
    display_order: parseInt(form.display_order, 10) || 999,
    lat:  form.lat  !== '' ? parseFloat(form.lat)  : null,
    lng:  form.lng  !== '' ? parseFloat(form.lng)  : null,
    cover_video: form.cover_video || null,
  };
}

function toChildrenPayload(ch) {
  return {
    gallery: ch.gallery
      .filter((g) => g.url.trim())
      // eslint-disable-next-line no-unused-vars
      .map(({ _id, ...g }) => ({
        url:        g.url.trim(),
        media_type: g.media_type || 'image',
        ...(g.poster_url?.trim() ? { poster_url: g.poster_url.trim() } : {}),
        ...(g.thumb_url?.trim()  ? { thumb_url:  g.thumb_url.trim()  } : {}),
      })),
    features: ch.features
      .filter((f) => f.text.trim())
      // eslint-disable-next-line no-unused-vars
      .map(({ _id, ...f }) => ({ text: f.text.trim() })),
    materials: ch.materials
      .filter((m) => m.title.trim())
      // eslint-disable-next-line no-unused-vars
      .map(({ _id, ...m }) => ({
        title: m.title.trim(),
        ...(m.description?.trim() ? { description: m.description.trim() } : {}),
        ...(m.brand_image?.trim() ? { brand_image:  m.brand_image.trim() } : {}),
      })),
    nearby: ch.nearby
      .filter((n) => n.text.trim())
      // eslint-disable-next-line no-unused-vars
      .map(({ _id, ...n }) => ({ text: n.text.trim() })),
    floor_plans: (ch.floor_plan_groups || []).flatMap((group) =>
      (group.plans || [])
        .filter((p) => p.image_url.trim())
        // eslint-disable-next-line no-unused-vars
        .map(({ _id: _pid, ...p }) => {
          const heading = p.label?.trim() || '';
          const cleanDetails = (p.details || [])
            // eslint-disable-next-line no-unused-vars
            .map(({ _id: _did, ...d }) => cleanDetailRow(d.name, d.size))
            .filter((d) => d.name);

          // Encode heading + details into label as JSON so they survive even
          // if the JSONB details column hasn't been migrated yet in Supabase.
          const encodedLabel = (heading || cleanDetails.length)
            ? JSON.stringify({ h: heading, d: cleanDetails })
            : null;

          return {
            image_url: p.image_url.trim(),
            ...(group.type_title?.trim() ? { type_title: group.type_title.trim() } : {}),
            ...(encodedLabel              ? { label:      encodedLabel             } : {}),
            details: cleanDetails, // also sent for when JSONB column exists
          };
        })
    ),
  };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProjectFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form,     setForm]     = useState(EMPTY_FORM);
  const [children, setChildren] = useState(EMPTY_CHILDREN);
  const [loading,  setLoading]  = useState(isEdit);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    getProject(id)
      .then((data) => {
        setForm(toForm(data));
        setChildren(toChildren(data));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // ── Form field helpers ──────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleStatusChange = (e) => {
    setForm((f) => ({ ...f, status: e.target.value }));
  };

  // ── Children helpers ────────────────────────────────────────────────────────
  const childAdd     = useCallback((key, empty) => setChildren((c) => ({ ...c, [key]: [...c[key], { ...empty, _id: genId() }] })), []);
  const childRemove  = useCallback((key, idx) => setChildren((c) => ({ ...c, [key]: c[key].filter((_, i) => i !== idx) })), []);
  const childUpdate  = useCallback((key, idx, field, value) =>
    setChildren((c) => ({
      ...c,
      [key]: c[key].map((item, i) => i === idx ? { ...item, [field]: value } : item),
    })), []);
  const childReorder = useCallback((key, newItems) => setChildren((c) => ({ ...c, [key]: newItems })), []);

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      let projectId = id;

      if (isEdit) {
        await updateProject(id, payload);
      } else {
        const created = await createProject(payload);
        projectId = created.id;
      }

      await updateProjectChildren(projectId, toChildrenPayload(children));

      toast.success(isEdit ? 'Proje güncellendi.' : 'Proje oluşturuldu.');
      if (!isEdit) {
        navigate(`/admin/projects/${projectId}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;
  if (error)   return <PageError message={error} />;

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-7">
        <Link
          to="/admin/projects"
          className="p-1.5 rounded-lg text-white/35 hover:text-white/70 hover:bg-white/[0.05] transition-colors"
        >
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">
            {isEdit ? 'Projeyi Düzenle' : 'Yeni Proje'}
          </h1>
          {isEdit && <p className="text-white/30 text-xs mt-0.5">{form.slug}</p>}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ── Temel Bilgiler ─────────────────────────────────────── */}
        <Section title="Temel Bilgiler">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Slug *">
              <input
                name="slug"
                value={form.slug}
                onChange={handleChange}
                onBlur={(e) => {
                  const n = normalizeSlug(e.target.value);
                  if (n !== form.slug) setForm((f) => ({ ...f, slug: n }));
                }}
                required
                placeholder="ober-prestij-konya"
                className={INPUT}
              />
            </Field>
            <Field label="Başlık *">
              <input name="title" value={form.title} onChange={handleChange} required
                placeholder="Ober Prestij" className={INPUT} />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Durum *">
              <select name="status" value={form.status} onChange={handleStatusChange} className={INPUT}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-[#1a1a1a]">{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Yıl *">
              <input name="year" value={form.year} onChange={handleChange} required
                placeholder="2027" className={INPUT} />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Lokasyon *">
              <select name="location" value={form.location} onChange={handleChange} className={INPUT}>
                {LOCATION_OPTIONS.map((l) => (
                  <option key={l} value={l} className="bg-[#1a1a1a]">{l}</option>
                ))}
              </select>
            </Field>
            <Field label="Proje Tipi">
              <select name="type" value={form.type} onChange={handleChange} className={INPUT}>
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t} className="bg-[#1a1a1a]">{t}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Konut / Ünite">
              <input name="units" value={form.units} onChange={handleChange}
                placeholder="2+1 / 3+1 Daire Seçenekleri" className={INPUT} />
            </Field>
            <Field label="Alan">
              <input name="area" value={form.area} onChange={handleChange}
                placeholder="17.000 m²" className={INPUT} />
            </Field>
          </div>

          <Field label="Adres">
            <input name="address" value={form.address} onChange={handleChange}
              placeholder="Karatay/Konya" className={INPUT} />
          </Field>

          <Field label="Kısa Açıklama">
            <textarea name="short_description" value={form.short_description} onChange={handleChange}
              rows={2} placeholder="Proje kartında görünecek 1-2 cümle" className={INPUT + ' resize-none'} />
          </Field>
        </Section>

        {/* ── Görsel & Yayın ─────────────────────────────────────── */}
        <Section title="Görsel ve Yayın">
          <Field label="Kapak Görseli">
            <UploadField
              value={form.cover_image}
              onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))}
              accept="image"
              folder="projects"
              placeholder="/images/... yolu veya Supabase URL'si"
            />
          </Field>

          <Field label="Kapak Video (isteğe bağlı)">
            <UploadField
              value={form.cover_video}
              onChange={(url) => setForm((f) => ({ ...f, cover_video: url }))}
              onBlur={(e) => {
                const embed = toYouTubeEmbed(e.target.value);
                if (embed !== e.target.value) setForm((f) => ({ ...f, cover_video: embed }));
              }}
              accept="video"
              folder="projects"
              placeholder="MP4 yolu (önerilir) veya YouTube linki (watch, shorts, youtu.be, embed)"
            />
            {(() => {
              const thumb = getYouTubeThumbnail(form.cover_video);
              return thumb ? (
                <div className="flex items-center gap-2 mt-2">
                  <img src={thumb} alt="YouTube önizleme" className="h-14 rounded object-cover border border-white/[0.08]" />
                  <span className="text-[10px] text-white/25">YouTube Önizleme</span>
                </div>
              ) : null;
            })()}
            <p className="text-[10px] text-white/20 mt-1.5">
              En temiz sonuç için MP4 yükleyin. YouTube embed desteklenir ancak platform
              bindirmeleri (kontroller, hesap uyarısı) video tipine/hesaba göre görünebilir.
            </p>
          </Field>

          <div className="flex items-end gap-4">
            <Field label="Proje Sırası">
              <input type="number" name="display_order" value={form.display_order}
                onChange={handleChange} min={1} className={INPUT + ' w-28'} />
              <p className="text-[10px] text-white/25 mt-1">
                Düşük değer = üstte. Kaydet sonrası otomatik normalize edilir.
              </p>
            </Field>
            <div className="pb-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" name="is_published" checked={form.is_published} onChange={handleChange}
                  className="accent-[#c5a059] w-3.5 h-3.5" />
                <span className="text-sm text-white/65">Yayında</span>
              </label>
            </div>
          </div>
        </Section>

        {/* ── Açıklamalar ────────────────────────────────────────── */}
        <Section title="Açıklamalar">
          <Field label="Detay Açıklaması">
            <textarea name="description" value={form.description} onChange={handleChange}
              rows={5} placeholder="Proje detay sayfasında görünecek uzun açıklama…"
              className={INPUT + ' resize-none'} />
          </Field>
        </Section>

        {/* ── Galeri ─────────────────────────────────────────────── */}
        <Section title="Galeri">
          <p className="text-[10px] text-white/30 -mt-2 mb-1">
            Görsel, MP4 video veya YouTube embed ekleyebilirsiniz. Tutup sürükleyerek sıralayın.
          </p>
          <div className="space-y-3">
            <SortableList
              items={children.gallery}
              onReorder={(items) => childReorder('gallery', items)}
              renderItem={(item, idx, dragHandleProps) => (
                <GalleryItemRow
                  item={item}
                  onChange={(field, val) => childUpdate('gallery', idx, field, val)}
                  onRemove={() => childRemove('gallery', idx)}
                  dragHandleProps={dragHandleProps}
                />
              )}
            />
          </div>
          <AddButton onClick={() => childAdd('gallery', { url: '', media_type: 'image', poster_url: '', thumb_url: '' })}
            label="Galeri öğesi ekle" />
        </Section>

        {/* ── Özellikler ─────────────────────────────────────────── */}
        <Section title="Özellikler">
          <p className="text-[10px] text-white/30 -mt-2 mb-1">
            İkon listesi — her satır ayrı bir özellik (örn. "7/24 Site Güvenliği"). Tutup sürükleyerek sıralayın.
          </p>
          <div className="space-y-2">
            <SortableList
              items={children.features}
              onReorder={(items) => childReorder('features', items)}
              renderItem={(item, idx, dragHandleProps) => (
                <div className="flex gap-2 items-center">
                  <DragHandle {...dragHandleProps} />
                  <input
                    value={item.text}
                    onChange={(e) => childUpdate('features', idx, 'text', e.target.value)}
                    placeholder="Özellik açıklaması"
                    className={INPUT + ' flex-1'}
                  />
                  <RemoveBtn onClick={() => childRemove('features', idx)} />
                </div>
              )}
            />
          </div>
          <AddButton onClick={() => childAdd('features', { text: '' })} label="Özellik ekle" />
        </Section>

        {/* ── Malzeme & Markalar ─────────────────────────────────── */}
        <Section title="Malzeme & Markalar">
          <p className="text-[10px] text-white/30 -mt-2 mb-1">
            Marka/malzeme kartları — her kart başlık ve açıklamadan oluşur. Tutup sürükleyerek sıralayın.
          </p>
          <div className="space-y-3">
            <SortableList
              items={children.materials}
              onReorder={(items) => childReorder('materials', items)}
              renderItem={(item, idx, dragHandleProps) => (
                <div className="flex gap-2 items-start">
                  <DragHandle {...dragHandleProps} />
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      value={item.title}
                      onChange={(e) => childUpdate('materials', idx, 'title', e.target.value)}
                      placeholder="Marka / Malzeme adı"
                      className={INPUT}
                    />
                    <input
                      value={item.description}
                      onChange={(e) => childUpdate('materials', idx, 'description', e.target.value)}
                      placeholder="Kısa açıklama"
                      className={INPUT}
                    />
                  </div>
                  <RemoveBtn onClick={() => childRemove('materials', idx)} />
                </div>
              )}
            />
          </div>
          <AddButton onClick={() => childAdd('materials', { title: '', description: '', brand_image: '' })}
            label="Malzeme/marka ekle" />
        </Section>

        {/* ── Teknik Detaylar ────────────────────────────────────── */}
        <Section title="Teknik Detaylar">
          <p className="text-[10px] text-white/30 -mt-2 mb-1">
            Boş bırakılan alanlar proje detay sayfasında görünmez.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="İnşaat Alanı">
              <input name="construction_area" value={form.construction_area} onChange={handleChange}
                placeholder="8.000 m²" className={INPUT} />
            </Field>
            <Field label="Yeşil Alan">
              <input name="green_area" value={form.green_area} onChange={handleChange}
                placeholder="2.000 m²" className={INPUT} />
            </Field>
            <Field label="Blok / Villa">
              <input name="blocks" value={form.blocks} onChange={handleChange}
                placeholder="8 Villa" className={INPUT} />
            </Field>
            <Field label="Ticari Alan">
              <input name="shop_count" value={form.shop_count} onChange={handleChange}
                placeholder="5 Ticari Alan" className={INPUT} />
            </Field>
            <Field label="Kat Sayısı">
              <input name="floors" value={form.floors} onChange={handleChange}
                placeholder="6 Kat" className={INPUT} />
            </Field>
            <Field label="Teslim">
              <input name="delivery" value={form.delivery} onChange={handleChange}
                placeholder="Yapım aşamasında - 2027" className={INPUT} />
            </Field>
          </div>
        </Section>

        {/* ── Yakın Çevre ────────────────────────────────────────── */}
        <Section title="Yakın Çevre">
          <div className="space-y-2">
            {children.nearby.map((item, idx) => (
              <div key={item._id} className="flex gap-2 items-center">
                <input
                  value={item.text}
                  onChange={(e) => childUpdate('nearby', idx, 'text', e.target.value)}
                  placeholder="Meram Tıp Hastanesi - 2 km"
                  className={INPUT + ' flex-1'}
                />
                <RemoveBtn onClick={() => childRemove('nearby', idx)} />
              </div>
            ))}
          </div>
          <AddButton onClick={() => childAdd('nearby', { text: '' })} label="Konum ekle" />
        </Section>

        {/* ── Kat Planları ───────────────────────────────────────── */}
        <Section title="Kat Planları (isteğe bağlı)">
          <p className="text-[10px] text-white/30 -mt-2 mb-1">
            Her grup bir daire tipidir (örn. 2+1, 3+1). Grup içine birden fazla plan/görsel eklenebilir.
          </p>
          <div className="space-y-4">
            {children.floor_plan_groups.map((group, gi) => (
              <FloorPlanGroupItem
                key={group._id}
                group={group}
                onGroupChange={(field, val) =>
                  setChildren((c) => ({
                    ...c,
                    floor_plan_groups: c.floor_plan_groups.map((g, i) =>
                      i === gi ? { ...g, [field]: val } : g
                    ),
                  }))
                }
                onGroupRemove={() =>
                  setChildren((c) => ({
                    ...c,
                    floor_plan_groups: c.floor_plan_groups.filter((_, i) => i !== gi),
                  }))
                }
              />
            ))}
          </div>
          <AddButton
            onClick={() =>
              setChildren((c) => ({
                ...c,
                floor_plan_groups: [
                  ...c.floor_plan_groups,
                  { _id: genId(), type_title: '', plans: [{ _id: genId(), image_url: '', label: '', details: [] }] },
                ],
              }))
            }
            label="Yeni tip/grup ekle (örn. 3+1 Daire)"
          />
        </Section>

        {/* ── SEO ───────────────────────────────────────────────── */}
        <Section title="SEO">
          <Field label="SEO Başlığı">
            <input name="seo_title" value={form.seo_title} onChange={handleChange}
              placeholder="Ober Prestij Konya | Ober Yapı" className={INPUT} />
          </Field>
          <Field label="SEO Açıklaması">
            <textarea name="seo_description" value={form.seo_description} onChange={handleChange}
              rows={2} className={INPUT + ' resize-none'} />
          </Field>
        </Section>

        {/* ── Harita ────────────────────────────────────────────── */}
        <Section title="Harita Koordinatları">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Enlem (lat)">
              <input type="number" step="any" name="lat" value={form.lat} onChange={handleChange}
                placeholder="37.884600" className={INPUT} />
            </Field>
            <Field label="Boylam (lng)">
              <input type="number" step="any" name="lng" value={form.lng} onChange={handleChange}
                placeholder="32.512594" className={INPUT} />
            </Field>
          </div>
        </Section>

        {/* ── Submit ───────────────────────────────────────────── */}
        <div className="flex items-center justify-between pt-2">
          <Link to="/admin/projects" className="text-sm text-white/35 hover:text-white/60 transition-colors">
            ← Geri Dön
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-[#c5a059] hover:bg-[#d4af6a] disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors"
          >
            <Save size={14} />
            {saving ? 'Kaydediliyor…' : isEdit ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Gallery item row ──────────────────────────────────────────────────────────

function GalleryItemRow({ item, onChange, onRemove, dragHandleProps }) {
  const needsPoster = item.media_type === 'video' || item.media_type === 'youtube';

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 space-y-2">
      <div className="flex flex-wrap gap-2 items-center">
        {/* Drag handle */}
        <DragHandle {...dragHandleProps} />

        {/* Type selector */}
        <select
          value={item.media_type}
          onChange={(e) => onChange('media_type', e.target.value)}
          className="bg-white/[0.04] border border-white/[0.08] rounded px-2 py-1.5 text-xs text-white/70 focus:outline-none focus:border-[#c5a059]/50 w-32 flex-shrink-0"
        >
          {GALLERY_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} className="bg-[#1a1a1a]">{o.label}</option>
          ))}
        </select>

        {/* URL */}
        <input
          value={item.url}
          onChange={(e) => onChange('url', e.target.value)}
          onBlur={item.media_type === 'youtube'
            ? (e) => { const embed = toYouTubeEmbed(e.target.value); if (embed !== e.target.value) onChange('url', embed); }
            : undefined
          }
          placeholder={
            item.media_type === 'image'   ? '/images/.../foto.png veya yükle →' :
            item.media_type === 'video'   ? '/images/.../video.mp4 veya yükle →' :
            'YouTube linki veya embed URL — blur\'da otomatik dönüştürülür'
          }
          className={INPUT + ' flex-1 text-xs py-1.5'}
        />

        {/* Upload button — not shown for YouTube embeds */}
        {item.media_type !== 'youtube' && (
          <UploadButton
            onChange={(url) => onChange('url', url)}
            accept={item.media_type === 'video' ? 'video' : 'image'}
            folder="gallery"
          />
        )}

        <RemoveBtn onClick={onRemove} />
      </div>

      {/* YouTube thumbnail preview */}
      {item.media_type === 'youtube' && (() => {
        const thumb = getYouTubeThumbnail(item.url);
        return thumb ? (
          <div className="flex items-center gap-2 pt-0.5">
            <img src={thumb} alt="YouTube önizleme" className="h-14 rounded object-cover border border-white/[0.08]" />
            <span className="text-[10px] text-white/25">Önizleme</span>
          </div>
        ) : null;
      })()}

      {needsPoster && item.media_type !== 'youtube' && (
        <div className="flex gap-2">
          <input
            value={item.poster_url}
            onChange={(e) => onChange('poster_url', e.target.value)}
            placeholder="Poster/kapak görseli URL (isteğe bağlı)"
            className={INPUT + ' flex-1 text-xs py-1.5'}
          />
          <input
            value={item.thumb_url}
            onChange={(e) => onChange('thumb_url', e.target.value)}
            placeholder="Küçük resim URL (isteğe bağlı)"
            className={INPUT + ' flex-1 text-xs py-1.5'}
          />
        </div>
      )}
    </div>
  );
}

// ── Floor plan group (e.g. "2+1 Daire Tipi") ─────────────────────────────────

function FloorPlanGroupItem({ group, onGroupChange, onGroupRemove }) {
  const plans = group.plans || [];

  const addPlan = () =>
    onGroupChange('plans', [...plans, { _id: genId(), image_url: '', label: '', details: [] }]);

  const removePlan = (idx) =>
    onGroupChange('plans', plans.filter((_, i) => i !== idx));

  const updatePlan = (idx, field, val) =>
    onGroupChange('plans', plans.map((p, i) => i === idx ? { ...p, [field]: val } : p));

  return (
    <div className="bg-white/[0.03] border border-[#c5a059]/15 rounded-xl p-4 space-y-4">
      {/* Group header: type title + remove group */}
      <div className="flex gap-2 items-center">
        <div className="flex-1">
          <label className="block text-[10px] text-white/30 uppercase tracking-[0.15em] mb-1.5">
            Grup / Tip Başlığı
          </label>
          <input
            value={group.type_title}
            onChange={(e) => onGroupChange('type_title', e.target.value)}
            placeholder="örn. 2+1 Daire Tipi"
            className={INPUT + ' font-medium'}
          />
        </div>
        <RemoveBtn onClick={onGroupRemove} />
      </div>

      {/* Plans inside this group */}
      <div className="space-y-3 pl-3 border-l border-[#c5a059]/10">
        {plans.map((plan, pi) => (
          <FloorPlanCard
            key={plan._id || pi}
            plan={plan}
            planIndex={pi}
            totalPlans={plans.length}
            onChange={(field, val) => updatePlan(pi, field, val)}
            onRemove={plans.length > 1 ? () => removePlan(pi) : null}
          />
        ))}
        <AddButton onClick={addPlan} label="Bu gruba plan/görsel ekle" />
      </div>
    </div>
  );
}

// ── Single floor plan card (image + heading + room rows) ──────────────────────

function FloorPlanCard({ plan, planIndex, totalPlans, onChange, onRemove }) {
  const details = plan.details || [];

  const addDetail = () =>
    onChange('details', [...details, { _id: genId(), name: '', size: '' }]);

  const updateDetail = (idx, field, val) =>
    onChange('details', details.map((d, i) => i === idx ? { ...d, [field]: val } : d));

  const removeDetail = (idx) =>
    onChange('details', details.filter((_, i) => i !== idx));

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 space-y-3">
      {/* Card header: plan number + optional remove */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-white/25 uppercase tracking-[0.15em]">
          Plan {planIndex + 1}{totalPlans > 1 ? ` / ${totalPlans}` : ''}
        </span>
        {onRemove && <RemoveBtn onClick={onRemove} />}
      </div>

      {/* Image drag-drop upload */}
      <UploadField
        value={plan.image_url}
        onChange={(url) => onChange('image_url', url)}
        accept="image"
        folder="floor-plans"
        placeholder="Kat planı görseli — sürükle-bırak veya URL"
      />

      {/* Floor/plan heading label */}
      <div>
        <label className="block text-[10px] text-white/25 uppercase tracking-[0.12em] mb-1">
          Kat / Plan Başlığı (isteğe bağlı)
        </label>
        <input
          value={plan.label}
          onChange={(e) => onChange('label', e.target.value)}
          placeholder="örn. Zemin Kat  •  1. Kat  •  2. Kat"
          className={INPUT + ' text-sm'}
        />
      </div>

      {/* Room detail rows */}
      <div>
        <label className="block text-[10px] text-white/25 uppercase tracking-[0.12em] mb-2">
          Oda / Alan Listesi
        </label>
        {details.length > 0 && (
          <div className="flex gap-2 mb-1 px-0.5">
            <span className="flex-[3] min-w-0 text-[9px] text-white/15 uppercase tracking-[0.12em]">Oda / Alan adı</span>
            <span className="flex-[2] min-w-0 text-[9px] text-white/15 uppercase tracking-[0.12em]">Ölçü</span>
            <span className="w-7 shrink-0" />
          </div>
        )}
        <div className="space-y-1.5">
          {details.map((d, idx) => (
            <div key={d._id || idx} className="flex gap-2 items-center">
              <input
                value={d.name}
                onChange={(e) => updateDetail(idx, 'name', e.target.value)}
                placeholder="Oda / Alan adı — örn. Salon"
                className={INPUT + ' flex-[3] min-w-0 text-sm'}
              />
              <input
                value={d.size}
                onChange={(e) => updateDetail(idx, 'size', e.target.value)}
                placeholder="Ölçü — örn. 25 m²"
                className={INPUT + ' flex-[2] min-w-0 text-sm'}
              />
              <RemoveBtn onClick={() => removeDetail(idx)} />
            </div>
          ))}
          <button
            type="button"
            onClick={addDetail}
            className="flex items-center gap-1.5 text-[11px] text-[#c5a059]/50 hover:text-[#c5a059] transition-colors mt-1.5"
          >
            <Plus size={12} /> Oda / Alan ekle
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

const INPUT =
  'w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/15 focus:outline-none focus:border-[#c5a059]/50 focus:bg-white/[0.06] transition-all';

function Section({ title, children }) {
  return (
    <div className="bg-[#111] border border-white/[0.07] rounded-xl p-5 space-y-4">
      <h2 className="text-[10px] text-white/30 uppercase tracking-[0.18em] pb-1 border-b border-white/[0.05]">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
        {label}
      </label>
      {children}
    </div>
  );
}

function RemoveBtn({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-1.5 text-white/25 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
    >
      <X size={14} />
    </button>
  );
}

function AddButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 text-xs text-[#c5a059]/60 hover:text-[#c5a059] transition-colors mt-1"
    >
      <Plus size={13} />
      {label}
    </button>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="w-5 h-5 border-2 border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
    </div>
  );
}

function PageError({ message }) {
  return (
    <div className="p-8">
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4 text-red-400 text-sm">
        Hata: {message}
      </div>
    </div>
  );
}
